/**
 * PaymentMethodsPage - Shows payment methods with Edit action
 */

import React, { useMemo, useState } from "react";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { PageTitle } from "../components/shared/PageTitle";
import { QuizButton } from "../components/shared/QuizButton";
import { EditPaymentModal } from "../components/EditPaymentModal";
import { ACCOUNT_COLORS } from "../theme/colors";

type PaymentDetails = {
  brand?: string;
  last4?: string;
  exp_month?: number | string;
  exp_year?: number | string;
};

type PaymentLike = {
  id?: string | number;
  customer_id?: string | number;
  default?: boolean;
  payment_type?: string;
  processor_name?: string;
  status?: string;
  status_reason?: string | null;
  payment_details?: PaymentDetails;
  created_at?: string;
  updated_at?: string;
};

// --------------------------------------
// Helpers
// --------------------------------------
function clean(x: any) {
  return String(x ?? "").trim();
}

function normalizePaymentMethods(payload: any): PaymentLike[] {
  if (!payload) return [];

  const list =
    (Array.isArray(payload) && payload) ||
    payload?.payment_methods ||
    payload?.data?.payment_methods ||
    payload?.data ||
    payload?.payment_methods?.data ||
    [];

  if (!Array.isArray(list)) return [];

  const out = list
    .map((x) => (x && typeof x === "object" ? (x as PaymentLike) : null))
    .filter(Boolean) as PaymentLike[];

  // Default first, then most recently updated
  out.sort((a, b) => {
    const da = a?.default ? 1 : 0;
    const db = b?.default ? 1 : 0;
    if (db !== da) return db - da;

    const ta = new Date(a?.updated_at || a?.created_at || 0).getTime();
    const tb = new Date(b?.updated_at || b?.created_at || 0).getTime();
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });

  return out;
}

function fmtExp(details?: PaymentDetails) {
  const m = clean(details?.exp_month);
  const y = clean(details?.exp_year);
  if (!m && !y) return "—";
  const mm = m ? m.padStart(2, "0") : "—";
  return `${mm}/${y || "—"}`;
}

function paymentLabel(p: PaymentLike) {
  const type = clean(p?.payment_type).toUpperCase();

  if (type.includes("PAYPAL")) return "PayPal";
  if (type.includes("CREDIT") || type.includes("CARD")) return "Card";

  if (type) return type.replace(/_/g, " ");

  return "Payment method";
}

function brandLabel(brand?: string) {
  const b = clean(brand).toLowerCase();
  if (!b) return "Card";
  return b.charAt(0).toUpperCase() + b.slice(1);
}

function statusChip(p: PaymentLike) {
  const s = clean(p?.status || "unknown").toLowerCase();
  const isOk = s === "valid" || s === "active";
  const label =
    isOk
      ? "Valid"
      : s === "invalid"
      ? "Invalid"
      : s === "expired"
      ? "Expired"
      : s === "failed"
      ? "Failed"
      : s;

  const bgColor = isOk
    ? "#E5FFEA"
    : s === "failed" || s === "invalid"
    ? "#FFE5E2"
    : "#FFF0D6";

  const textColor = isOk
    ? "#2E7D32"
    : s === "failed" || s === "invalid"
    ? "#D14C3F"
    : "#CE7A00";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        background: bgColor,
        color: textColor,
        fontWeight: 700,
        textTransform: "capitalize",
        height: 28,
        "& .MuiChip-label": { px: 1.5, fontSize: 12 },
      }}
    />
  );
}

function defaultChip(isDefault?: boolean) {
  if (!isDefault) return null;
  return (
    <Chip
      size="small"
      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
      label="Default"
      sx={{
        background: "#E5FFEA",
        color: "#2E7D32",
        fontWeight: 700,
        height: 24,
        "& .MuiChip-label": { px: 1.25, fontSize: 11 },
        "& .MuiChip-icon": { ml: 0.5 },
      }}
    />
  );
}

// --------------------------------------
// Page
// --------------------------------------
export function PaymentMethodsPage({
  payments,
  onSendUpdateEmail,
}: {
  payments: any;
  onSendUpdateEmail: (paymentMethodId?: string) => Promise<{ ok: boolean; error?: string }>;
}) {
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentLike | null>(null);

  const list = useMemo(() => normalizePaymentMethods(payments), [payments]);
  const primary = list[0] || null;

  const handleOpenEditModal = (payment: PaymentLike) => {
    setSelectedPayment(payment);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedPayment(null);
  };

  const subtitle =
    primary?.processor_name === "shopify_payments"
      ? "Your subscription is billed securely via Shopify Payments"
      : "Your subscription is billed securely through Recharge";

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <QuizStyledCard>
        <Box sx={{ mb: 3 }}>
          <PageTitle title="Payment Methods" subtitle={subtitle} />
        </Box>

        {list.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 4, md: 6 },
            }}
          >
            <Box
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: "50%",
                backgroundColor: ACCOUNT_COLORS.coralLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CreditCardIcon
                sx={{ fontSize: { xs: 40, md: 50 }, color: ACCOUNT_COLORS.primary }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 800,
                color: ACCOUNT_COLORS.textPrimary,
                mb: 1,
              }}
            >
              No payment method found
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: ACCOUNT_COLORS.textPrimary,
                opacity: 0.75,
                mb: 3,
              }}
            >
              Add a payment method to continue with your subscription
            </Typography>
          </Box>
        ) : (
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            {list.map((p, idx) => {
              const id = clean(p?.id) || `pm-${idx}`;
              const details = p?.payment_details;
              const last4 = clean(details?.last4) || "—";
              const brand = brandLabel(details?.brand);
              const exp = fmtExp(details);

              const showWarning =
                clean(p?.status || "").toLowerCase() &&
                clean(p?.status || "").toLowerCase() !== "valid";

              return (
                <QuizStyledCard variant="white" key={id}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          flexWrap: "wrap",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: 18, md: 20 },
                            fontWeight: 800,
                            color: ACCOUNT_COLORS.textPrimary,
                          }}
                        >
                          {paymentLabel(p)}
                        </Typography>
                        {defaultChip(!!p?.default)}
                      </Box>

                      <Typography
                        sx={{
                          fontSize: { xs: 15, md: 16 },
                          color: ACCOUNT_COLORS.textPrimary,
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {brand} •••• {last4}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: 13, md: 14 },
                          color: ACCOUNT_COLORS.textPrimary,
                          opacity: 0.65,
                        }}
                      >
                        Expires {exp}
                      </Typography>

                      {showWarning && (
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            gap: 1,
                            alignItems: "flex-start",
                            p: 1.5,
                            borderRadius: "8px",
                            background: "#FFF0D6",
                          }}
                        >
                          <WarningAmberIcon
                            sx={{ fontSize: 18, color: "#CE7A00", mt: 0.25 }}
                          />
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#CE7A00",
                              fontWeight: 600,
                            }}
                          >
                            This payment method may need attention
                            {p?.status_reason ? `: ${clean(p.status_reason)}` : "."}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {statusChip(p)}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Edit Button */}
                  <QuizButton
                    variant="secondary"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => handleOpenEditModal(p)}
                  >
                    Edit
                  </QuizButton>
                </QuizStyledCard>
              );
            })}
          </Stack>
        )}
      </QuizStyledCard>

      {/* Edit Payment Modal */}
      <EditPaymentModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        onSendEmail={onSendUpdateEmail}
        paymentInfo={selectedPayment ? {
          id: selectedPayment.id,
          brand: selectedPayment.payment_details?.brand,
          last4: selectedPayment.payment_details?.last4,
        } : null}
      />
    </Stack>
  );
}
